import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import StarIcon from '@mui/icons-material/Star';
import SectionBackground from './SectionBackground';

const TestimonialsSection: React.FC = () => {
  const { t } = useTranslation();
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  const testimonials = [
    {
      name: t('landing.testimonials.testimonial1.name'),
      role: t('landing.testimonials.testimonial1.role'),
      location: t('landing.testimonials.testimonial1.location'),
      rating: 5,
      text: t('landing.testimonials.testimonial1.text'),
      avatar: '👩‍💼',
      properties: t('landing.testimonials.testimonial1.properties'),
      saved: t('landing.testimonials.testimonial1.saved')
    },
    {
      name: t('landing.testimonials.testimonial2.name'),
      role: t('landing.testimonials.testimonial2.role'),
      location: t('landing.testimonials.testimonial2.location'),
      rating: 5,
      text: t('landing.testimonials.testimonial2.text'),
      avatar: '👨‍💼',
      properties: t('landing.testimonials.testimonial2.properties'),
      saved: t('landing.testimonials.testimonial2.saved')
    },
    {
      name: t('landing.testimonials.testimonial3.name'),
      role: t('landing.testimonials.testimonial3.role'),
      location: t('landing.testimonials.testimonial3.location'),
      rating: 5,
      text: t('landing.testimonials.testimonial3.text'),
      avatar: '👩‍🏫',
      properties: t('landing.testimonials.testimonial3.properties'),
      saved: t('landing.testimonials.testimonial3.saved')
    }
  ];

  return (
    <SectionBackground 
      variant="white"
      style={{
        padding: 'clamp(40px, 6vw, 60px) clamp(20px, 5vw, 40px)'
      }}>

      <div className="container testimonials-container" style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <div className="testimonials-header" style={{ textAlign: 'center', marginBottom: '60px' }}>
          <div className="testimonials-badge" style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 20px',
            background: 'rgba(0, 82, 255, 0.1)',
            border: '1px solid rgba(0, 82, 255, 0.3)',
            borderRadius: '100px',
            marginBottom: '30px'
          }}>
            <StarIcon sx={{ fontSize: 16, color: '#0052FF' }} />
            <span style={{
              color: '#0052FF',
              fontSize: 'clamp(0.8rem, 1.5vw, 0.9rem)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '2px'
            }}>
              {t('landing.testimonials.subtitle')}
            </span>
          </div>

          <h2 className="testimonials-title" style={{
            fontSize: 'clamp(2.5rem, 4vw, 3.5rem)',
            fontWeight: 700,
            marginBottom: '20px',
            color: '#0A0B0D'
          }}>
{t('landing.testimonials.title')}
          </h2>
        </div>

        {/* Cards Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(400px, 100%), 1fr))',
          gap: 'clamp(20px, 4vw, 40px)'
        }}>
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              style={{
                background: hoveredCard === index 
                  ? 'rgba(0, 82, 255, 0.1)'
                  : '#ffffff',
                border: '1px solid rgba(0, 82, 255, 0.1)',
                borderRadius: 'clamp(20px, 3vw, 30px)',
                padding: 'clamp(20px, 4vw, 40px)',
                cursor: 'pointer',
                // transition: 'all 0.3s ease',
                boxShadow: hoveredCard === index
                  ? '0 30px 60px rgba(0, 82, 255, 0.3)'
                  : '0 10px 30px rgba(0, 0, 0, 0.1)'
              }}
              onMouseEnter={() => setHoveredCard(index)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              {/* Rating */}
              <div style={{ marginBottom: '20px' }}>
                {[...Array(testimonial.rating)].map((_, i) => (
                  <span key={i} style={{ 
                    color: '#FFD700', 
                    fontSize: '1.3rem',
                    filter: 'drop-shadow(0 0 5px rgba(255, 215, 0, 0.5))'
                  }}>
                    ★
                  </span>
                ))}
              </div>

              {/* Quote */}
              <p style={{
                fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
                lineHeight: 1.8,
                color: '#5B616E',
                marginBottom: 'clamp(20px, 3vw, 30px)',
                fontStyle: 'italic'
              }}>
                "{testimonial.text}"
              </p>

              {/* Author */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(12px, 2vw, 15px)' }}>
                  <div style={{
                    width: 'clamp(40px, 6vw, 50px)',
                    height: 'clamp(40px, 6vw, 50px)',
                    background: 'linear-gradient(135deg, #0052FF 0%, #00D4FF 100%)',
                    borderRadius: 'clamp(12px, 2vw, 15px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 'clamp(1.2rem, 2.5vw, 1.5rem)'
                  }}>
                    {testimonial.avatar}
                  </div>
                  <div>
                    <div style={{ color: '#0A0B0D', fontWeight: 700, fontSize: 'clamp(1rem, 2vw, 1.1rem)' }}>
                      {testimonial.name}
                    </div>
                    <div style={{ color: '#5B616E', fontSize: 'clamp(0.8rem, 1.8vw, 0.9rem)' }}>
                      {testimonial.role} • {testimonial.location}
                    </div>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div style={{
                display: 'flex',
                gap: 'clamp(15px, 3vw, 20px)',
                marginTop: 'clamp(15px, 2.5vw, 20px)',
                paddingTop: 'clamp(15px, 2.5vw, 20px)',
                borderTop: '1px solid rgba(0, 82, 255, 0.1)',
                flexWrap: 'wrap'
              }}>
                <div>
                  <div style={{ color: '#5B616E', fontSize: '0.85rem' }}>
                    {t('landing.testimonials.portfolio')}
                  </div>
                  <div style={{ color: '#0052FF', fontWeight: 600 }}>
                    {testimonial.properties}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#5B616E', fontSize: '0.85rem' }}>
                    {t('landing.testimonials.saves')}
                  </div>
                  <div style={{ color: '#10B981', fontWeight: 600 }}>
                    {testimonial.saved}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .testimonials-header {
            margin-bottom: 0 !important;
          }

          .testimonials-badge {
            margin-bottom: 16px !important;
          }

          .testimonials-title {
            font-size: 2.5rem !important;
            margin-bottom: 0 !important;
          }
        }

        @media (max-width: 480px) {
          .testimonials-header {
            margin-bottom: 0 !important;
          }

          .testimonials-badge {
            margin-bottom: 12px !important;
          }

          .testimonials-title {
            font-size: 2rem !important;
            margin-bottom: 0 !important;
          }
        }
      `}</style>
    </SectionBackground>
  );
};

export default TestimonialsSection; 